import React, { useState } from 'react';

const CustomerProfileModal = ({ lead, leadId, onClose }) => {
  const [activeTab, setActiveTab] = useState('profile');

  // If no lead provided, show error
  if (!lead && !leadId) {
    return null;
  }

  // Use provided lead data directly (no API call needed)



  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'contacted': 'bg-yellow-100 text-yellow-800',
      'qualified': 'bg-purple-100 text-purple-800',
      'Quoted': 'bg-green-100 text-green-800',
      'negotiation': 'bg-orange-100 text-orange-800',
      'won': 'bg-green-100 text-green-800',
      'lost': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'bg-red-100 text-red-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'low': 'bg-green-100 text-green-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (!lead) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 rounded-t-lg">
          <div className="flex justify-between items-start">
            <div className="flex items-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mr-4">
                <span className="text-3xl font-bold text-blue-600">
                  {lead.customerName?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-bold">{lead.customerName}</h2>
                <p className="text-blue-100 text-sm">SR No: {lead.serialNumber || 'N/A'}</p>
                <p className="text-blue-100">{lead.email}</p>
                <p className="text-blue-100">{lead.contactNumber || lead.phone}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-3xl"
            >
              ×
            </button>
          </div>

          {/* Status Badges */}
          <div className="flex gap-3 mt-4">
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(lead.status)}`}>
              {lead.status?.toUpperCase() || 'NEW'}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getPriorityColor(lead.priority)}`}>
              {lead.priority?.toUpperCase() || 'MEDIUM'} PRIORITY
            </span>

          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-6 py-3 font-medium ${activeTab === 'profile'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
                }`}
            >
              📋 Profile Details
            </button>

            <button
              onClick={() => setActiveTab('followups')}
              className={`px-6 py-3 font-medium ${activeTab === 'followups'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
                }`}
            >
              📞 Follow-ups ({lead.followUps?.length || 0})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600">Company</label>
                    <p className="font-medium">{lead.customerCompany || lead.company || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Email</label>
                    <p className="font-medium">{lead.email}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Phone</label>
                    <p className="font-medium">{lead.contactNumber || lead.phone}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Address</label>
                    <p className="font-medium">{lead.address || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Tour Details */}
              <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                <h3 className="text-lg font-bold text-blue-800 mb-4">🎒 Tour Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-blue-700">Destination</label>
                    <p className="font-medium text-gray-900">{lead.destination || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-blue-700">Tour Type</label>
                    <p className="font-medium text-gray-900">{lead.tourType || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-blue-700">Tour Code</label>
                    <p className="font-medium text-gray-900">{lead.tourCode || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-blue-700">Travel Date</label>
                    <p className="font-medium text-gray-900">{formatDate(lead.travelDate)}</p>
                  </div>
                  <div>
                    <label className="text-sm text-blue-700">No. of Nights</label>
                    <p className="font-medium text-gray-900">{lead.numberOfNights || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-blue-700">No. of Pax</label>
                    <p className="font-medium text-gray-900">{lead.numberOfPax || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-blue-700">No. of Children</label>
                    <p className="font-medium text-gray-900">{lead.numberOfChildren || 0}</p>
                  </div>
                  <div>
                    <label className="text-sm text-blue-700">No. of Rooms</label>
                    <p className="font-medium text-gray-900">{lead.numberOfRooms || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-blue-700">Hotel Category</label>
                    <p className="font-medium text-gray-900">{lead.hotelCategory || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-blue-700">Meal Plan</label>
                    <p className="font-medium text-gray-900">{lead.mealPlan || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-blue-700">Extra Bed</label>
                    <p className="font-medium text-gray-900">{lead.extraBed || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-blue-700">Vehicle Type</label>
                    <p className="font-medium text-gray-900">{lead.vehicleType || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-blue-700">Pick-Up</label>
                    <p className="font-medium text-gray-900">{lead.pickUp || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-blue-700">Drop</label>
                    <p className="font-medium text-gray-900">{lead.drop || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-blue-700">Reservation Status</label>
                    <p className="font-medium text-gray-900">{lead.reservationStatus || 'Pending'}</p>
                  </div>
                </div>
                {lead.specialNote && (
                  <div className="mt-4 pt-4 border-t border-blue-200">
                    <label className="text-sm text-blue-700">Special Note</label>
                    <p className="font-medium text-gray-900 mt-1">{lead.specialNote}</p>
                  </div>
                )}
              </div>

              {/* Lead Details */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Lead Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600">Product of Interest</label>
                    <p className="font-medium">{lead.productOfInterest || lead.productCategory || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Sector</label>
                    <p className="font-medium">{lead.sector || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Source</label>
                    <p className="font-medium">{lead.sourceOfLead || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Budget</label>
                    <p className="font-medium">{lead.budget ? `₹${lead.budget}` : 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Lead Value</label>
                    <p className="font-medium">{lead.value ? `₹${lead.value.toLocaleString()}` : 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Created Date</label>
                    <p className="font-medium">{formatDate(lead.date || lead.createdAt)}</p>
                  </div>
                </div>
              </div>



              {/* Notes */}
              {lead.notes && (
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">Notes</h3>
                  <p className="text-gray-700 whitespace-pre-line">{lead.notes}</p>
                </div>
              )}
            </div>
          )}



          {/* Follow-ups Tab */}
          {activeTab === 'followups' && (
            <div className="space-y-4">
              {(!lead.followUps || lead.followUps.length === 0) ? (
                <div className="text-center py-12">
                  <span className="text-6xl">📞</span>
                  <p className="text-gray-600 mt-4">No follow-ups recorded yet</p>
                </div>
              ) : (
                lead.followUps.map((followUp, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-600">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">
                          {followUp.type === 'call' ? '📞' :
                            followUp.type === 'email' ? '📧' :
                              followUp.type === 'meeting' ? '🤝' :
                                followUp.type === 'demo' ? '🎯' :
                                  followUp.type === 'proposal' ? '📋' : '💬'}
                        </span>
                        <h4 className="font-bold text-gray-800 capitalize">{followUp.type}</h4>
                      </div>
                      <span className="text-sm text-gray-600">{formatDate(followUp.date)}</span>
                    </div>
                    <p className="text-gray-700 mb-2">{followUp.summary}</p>
                    {followUp.notes && (
                      <p className="text-sm text-gray-600 italic">{followUp.notes}</p>
                    )}
                    {followUp.nextFollowUpDate && (
                      <div className="mt-2 text-sm">
                        <span className="text-gray-600">Next Follow-up:</span>
                        <span className="ml-2 font-medium text-blue-600">{formatDate(followUp.nextFollowUpDate)}</span>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
          >
            Close Profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfileModal;
